/**
 * FA(3) 2025 tree selectors — helpery do bezpiecznego czytania pól z `parsed_tree`
 * zwróconego przez backend `GET /api/ksef/inbox/{n}` (klucz `parsed_tree`).
 *
 * Zasada: NIE zgadujemy. Jeśli pole nie istnieje w konkretnej fakturze
 * albo jest puste — zwracamy null/false, żeby UI mógł je pominąć.
 *
 * Parser bezstratny (`parse_fa3_xml_lossless`) zwraca:
 *   - pola jednokrotne z tekstem → string (może być pusty "")
 *   - pola jednokrotne z children → dict
 *   - pola wielokrotne (maxOccurs > 1) → lista (nawet gdy 1 element)
 *   - atrybuty → pod `@nazwa`
 *
 * Selektory tutaj NIE modyfikują wartości — zwracają je 1:1 z drzewa.
 * Formatowanie (kwoty, daty, etykiety enum) robi konsument.
 */

/** True jeśli wartość ma sens do wyświetlenia. Puste stringi, null, undefined → false. */
export const hasValue = (v) => {
  if (v === null || v === undefined) return false;
  if (typeof v === "string") return v.trim().length > 0;
  if (Array.isArray(v)) return v.length > 0;
  if (typeof v === "object") return Object.keys(v).length > 0;
  return true;
};

/** Bezpieczne pobranie zagnieżdżonego pola z drzewa. Zwraca undefined jeśli po drodze null/undefined. */
export const pick = (tree, ...path) => {
  let cur = tree;
  for (const key of path) {
    if (cur == null) return undefined;
    if (typeof cur !== "object") return undefined;
    cur = cur[key];
  }
  return cur;
};

/** Wymusza listę: null → [], obiekt → [obj], lista → lista. */
export const asList = (v) => {
  if (v == null) return [];
  return Array.isArray(v) ? v : [v];
};

/** String value trimmed lub null. Traktuje "0" jako valid! */
export const asStr = (v) => {
  if (v == null) return null;
  // Zabezpieczenie: obiekt/tablica NIE mogą trafić do UI jako "[object Object]".
  // Zwracamy null → pole zostaje ukryte (hasValue=false) zamiast pokazywać śmieci.
  if (typeof v === "object") return null;
  const s = String(v).trim();
  return s.length > 0 ? s : null;
};

/**
 * Rodzaj faktury (FA(3) enum) → PL human-readable.
 * Wartości enum wg XSD 2025: VAT | KOR | ZAL | ROZ | UPR | KOR_ZAL | KOR_ROZ
 */
export const RODZAJ_FAKTURY_LABEL = {
  VAT: "Faktura VAT",
  KOR: "Faktura korygująca",
  ZAL: "Faktura zaliczkowa",
  ROZ: "Faktura rozliczeniowa (końcowa)",
  UPR: "Faktura uproszczona",
  KOR_ZAL: "Korekta zaliczkowa",
  KOR_ROZ: "Korekta rozliczeniowa",
};

export const formatRodzajFaktury = (code) => {
  if (!code) return null;
  const s = String(code).trim();
  return RODZAJ_FAKTURY_LABEL[s] || s;
};

/**
 * Adnotacje FA(3) — semantyczne flagi na fakturze.
 * Zwraca listę { key, label } dla flag ustawionych na "1" (lub innej wartości truthy).
 * Nie wyświetlamy nic gdy sekcja pusta.
 */
const ADNOTACJE_LABELS = {
  P_16: "Metoda kasowa",
  P_17: "Samofakturowanie",
  P_18: "Odwrotne obciążenie",
  P_18A: "Mechanizm podzielonej płatności",
  P_22: "Wewnątrzwspólnotowa dostawa nowego środka transportu",
  P_23: "Procedura marży - dzieła sztuki",
  Zwolnienie: "Zwolnienie z podatku",
  NoweSrodkiTransportu: "Nowe środki transportu",
  PMarzy: "Procedura marży",
  ZwolnienieNowyPojazd: "Zwolnienie nowego pojazdu",
  PMarzy_2: "P. marży 2",
  PMarzy_3_1: "P. marży 3.1",
  PMarzy_3_2: "P. marży 3.2",
  PMarzy_3_3: "P. marży 3.3",
};

/** Rozpakowuje `Fa.Adnotacje` — pola z wartością "1" (=TAK dotyczy wg XSD TZnacznikMPP).
 *
 * XSD FA(3) 2025 semantyka:
 *  - Płaskie flagi (P_16, P_17, P_18, P_18A, P_23) mają wartości "1" (TAK) / "2" (NIE, default).
 *  - Zagnieżdżone struktury (Zwolnienie, NoweSrodkiTransportu, PMarzy) — pomijamy w tej wersji,
 *    bo wymagają dedykowanej analizy (np. Zwolnienie ma warianty P_19A/P_19B/P_19N gdzie N=nie
 *    dotyczy). Renderowanie ich tutaj wprowadziłoby fałszywie pozytywne badge.
 */
export const extractAdnotacje = (fa) => {
  const adn = pick(fa, "Adnotacje");
  if (!adn || typeof adn !== "object") return [];
  const out = [];
  for (const [k, v] of Object.entries(adn)) {
    if (k.startsWith("@")) continue;
    // Pomijamy zagnieżdżone struktury — zbyt złożone dla prostego badge.
    if (typeof v === "object" && v !== null) continue;
    const val = String(v ?? "").trim();
    // Wartość "1" wg XSD TZnacznikMPP = flaga włączona; "2" (default) lub pusto = wyłączona.
    if (val !== "1") continue;
    const label = ADNOTACJE_LABELS[k] || k;
    out.push({ key: k, label, value: val });
  }
  return out;
};

/**
 * Ekstrahuje wszystkie IBAN-y ze struktury `Fa.Platnosc.RachunekBankowy`.
 * Może być pojedynczy obiekt LUB lista (parser normalizuje wg XSD cardinality).
 * Zwraca: [{ nrRb, swift, nazwaBanku, opisRachunku, rachunekWlasnyBanku }]
 */
export const extractBankAccounts = (fa) => {
  const raw = pick(fa, "Platnosc", "RachunekBankowy");
  return asList(raw).map((rb) => ({
    nrRb: asStr(rb?.NrRB),
    swift: asStr(rb?.SWIFT),
    nazwaBanku: asStr(rb?.NazwaBanku),
    opisRachunku: asStr(rb?.OpisRachunku),
    rachunekWlasnyBanku: asStr(rb?.RachunekWlasnyBanku),
  })).filter((rb) => hasValue(rb.nrRb) || hasValue(rb.swift) || hasValue(rb.nazwaBanku) || hasValue(rb.opisRachunku));
};

/** Rachunek faktora (dla flag faktoringowej). */
export const extractFaktorAccount = (fa) => {
  const raw = pick(fa, "Platnosc", "RachunekBankowyFaktora");
  const rb = Array.isArray(raw) ? raw[0] : raw;
  if (!rb) return null;
  const result = {
    nrRb: asStr(rb.NrRB),
    swift: asStr(rb.SWIFT),
    nazwaBanku: asStr(rb.NazwaBanku),
    opisRachunku: asStr(rb.OpisRachunku),
  };
  return hasValue(result.nrRb) || hasValue(result.swift) ? result : null;
};

/**
 * Pobiera atrybut `Zaplacono` (0/1) w rozsądny sposób —
 * może być string "1"/"0" (najczęściej) LUB atrybut/pole wewnątrz Platnosc.
 */
export const isZaplacona = (fa) => {
  const z = pick(fa, "Platnosc", "Zaplacono");
  return z === "1" || z === 1 || z === true;
};

/** Adres jako `AdresL1\nAdresL2\nKodKraju` (multi-line dla whitespace-pre-line). */
export const formatAdres = (adres) => {
  if (!adres || typeof adres !== "object") return null;
  const parts = [];
  const l1 = asStr(adres.AdresL1);
  const l2 = asStr(adres.AdresL2);
  const kod = asStr(adres.KodKraju);
  if (l1) parts.push(l1);
  if (l2) parts.push(l2);
  if (kod) parts.push(kod);
  return parts.length > 0 ? parts.join("\n") : null;
};

/**
 * Ekstrahuje kody GTU z wiersza pozycji. FA(3) 2025 dopuszcza DWA sposoby:
 *  1. Poszczególne pola boolean `GTU_01`, `GTU_02`, ..., `GTU_13` — starszy wariant.
 *  2. Pojedyncze pole `GTU` zawierające kod (np. `"GTU_03"`) — wariant obserwowany w prod.
 *
 * Obsługujemy oba (co jest zgodne z tym, co parser zwraca).
 */
export const extractGtu = (wiersz) => {
  if (!wiersz || typeof wiersz !== "object") return [];
  const out = new Set();
  // Wariant 1: poszczególne pola
  for (let i = 1; i <= 13; i += 1) {
    const key = `GTU_${String(i).padStart(2, "0")}`;
    if (hasValue(wiersz[key])) out.add(key);
  }
  // Wariant 2: pole `GTU` (string lub lista)
  const gtu = wiersz.GTU;
  if (gtu != null) {
    asList(gtu).forEach((v) => {
      const s = asStr(v);
      if (s) out.add(s.startsWith("GTU_") ? s : `GTU_${s}`);
    });
  }
  return Array.from(out);
};

/** Ekstrahuje procedury dla pozycji (np. MPP, EE itd. — Procedura może być listą). */
export const extractProcedury = (wiersz) => {
  const raw = pick(wiersz, "Procedura");
  return asList(raw).map(asStr).filter(Boolean);
};
